import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { MenuOrSheet } from "@/components/ActionSheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { waUrl as waBase } from "@/lib/contacts";
import { twilioFor, sendWa } from "@/lib/whatsapp";
import { MessageCircle, Mail, UserCheck, AlertTriangle, KeyRound, Check } from "lucide-react";

const ROLE = { vendedor: "Proprietário", comprador: "Comprador" };
const CH = { whatsapp: "WhatsApp", email: "email" };
const dt = (iso) => new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const waUrl = (phone, text) => `${waBase(phone)}?text=${encodeURIComponent(text)}`;
const ids = (lk) => `${lk.role}-${lk.party_index}`;

function useSend(pid, lk, onChange) {
  return async (channel, text) => {
    const tw = channel === "whatsapp" && twilioFor(pid);
    const win = channel === "whatsapp" && !tw ? window.open("", "_blank") : null;
    try {
      const { data } = await api.post(`/processes/${pid}/portal-links/${lk.token}/send`, { channel, base_url: window.location.origin, text });
      if (tw) { await sendWa(pid, lk.phone, data.wa_text); onChange(); return; }
      if (win) win.location.href = waUrl(lk.phone, data.wa_text);
      toast.success(channel === "email" ? "Email enviado ao cliente" : channel === "consultor" ? "Pedido enviado ao consultor" : "WhatsApp aberto");
      onChange();
    } catch (e) { if (win) win.close(); toast.error(apiErr(e.response?.data?.detail)); }
  };
}

function ChannelButtons({ lk, send, text }) {
  const btn = "inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors";
  return (
    <div className="flex gap-2 flex-wrap">
      {lk.has_phone && <button onClick={() => send("whatsapp", text)} className={`${btn} bg-ec-green text-white hover:bg-ec-greenDark`} data-testid={`portal-wa-${ids(lk)}`}><MessageCircle size={13} /> WhatsApp</button>}
      {lk.has_email && <button onClick={() => send("email", text)} className={`${btn} border border-slate-200 text-ec-green hover:bg-slate-50`} data-testid={`portal-email-${ids(lk)}`}><Mail size={13} /> Email</button>}
    </div>
  );
}

function ReadyMessage({ pid, lk, send, title, text: given, testid }) {
  const [text, setText] = useState(given || null);
  useEffect(() => {
    if (given) { setText(given); return; }
    api.post(`/processes/${pid}/portal-links/${lk.token}/send`, { channel: "copy", base_url: window.location.origin, preview: true })
      .then(({ data }) => setText(data.wa_text)).catch(() => setText(""));
  }, [pid, lk.token, given]);
  if (text === "") return <p className="text-xs text-slate-500" data-testid={testid}>Ainda não foi enviada.</p>;
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 space-y-2" data-testid={testid}>
      <div className="text-xs font-semibold text-slate-700">{title}</div>
      <p className="text-xs text-slate-600 whitespace-pre-line max-h-56 overflow-y-auto" data-testid={`${testid}-text`}>{text === null ? "A preparar…" : text}</p>
      {text && <ChannelButtons lk={lk} send={send} text={given ? text : undefined} />}
    </div>
  );
}

function SentLine({ lk, last }) {
  if (!last) return null;
  const how = last.channel === "consultor" ? "Pedida ao consultor" : `Enviada por ${CH[last.channel] || last.channel}`;
  return <div className="text-xs text-slate-700 flex items-center gap-1.5" data-testid={`portal-sent-${ids(lk)}`}><Check size={13} className="text-emerald-600" /> {how} · {last.by_name} · {dt(last.at)}</div>;
}

function OpenLine({ lk }) {
  if (!lk.last_access_at) return <div className="text-xs text-slate-500 pl-5" data-testid={`portal-opened-${ids(lk)}`}>Ainda não abriu</div>;
  return (
    <div className="text-xs text-slate-700 flex items-center gap-1.5" data-testid={`portal-opened-${ids(lk)}`}>
      <Check size={13} className="text-emerald-600" /> Aberta pelo cliente · {dt(lk.last_access_at)}{lk.privacy_ack ? " · aviso de privacidade aceite" : ""}
    </div>
  );
}

function SentDialog({ last, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="portal-sent-message-dialog">
        <DialogHeader><DialogTitle className="font-heading text-ec-green">Mensagem enviada</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">{CH[last.channel] || last.channel} · {last.by_name} · {dt(last.at)}</p>
        <p className="text-sm text-slate-700 whitespace-pre-line bg-slate-50 rounded-lg p-3 max-h-[60vh] overflow-y-auto" data-testid="portal-sent-message-text">
          {last.text || "Esta mensagem foi enviada antes de as mensagens ficarem guardadas."}
        </p>
      </DialogContent>
    </Dialog>
  );
}

function SentActions({ lk, send, last }) {
  const [view, setView] = useState(false);
  const copy = () => navigator.clipboard.writeText(lk.url).then(() => toast.success("Link copiado")).catch(() => toast.error("Não foi possível copiar"));
  const items = [
    lk.has_phone && { label: "WhatsApp", icon: MessageCircle, onClick: () => send("whatsapp"), testid: "resend-whatsapp" },
    lk.has_email && { label: "Email", icon: Mail, onClick: () => send("email"), testid: "resend-email" },
    { label: "Pedir ao consultor que envie", icon: UserCheck, onClick: () => send("consultor"), testid: "resend-consultor" },
  ].filter(Boolean);
  const link = "underline underline-offset-2 hover:text-slate-900";
  return (
    <div className="flex gap-4 text-xs text-slate-500 pl-5">
      <MenuOrSheet title="Reenviar por" align="start" testid={`portal-resend-menu-${ids(lk)}`} items={items}
        trigger={<button className={link} data-testid={`portal-resend-${ids(lk)}`}>Reenviar</button>} />
      <button onClick={copy} className={link} data-testid={`portal-copy-${ids(lk)}`}>Copiar link</button>
      {last.channel !== "consultor" && <button onClick={() => setView(true)} className={link} data-testid={`portal-view-sent-${ids(lk)}`}>Ver mensagem enviada</button>}
      {view && <SentDialog last={last} onClose={() => setView(false)} />}
    </div>
  );
}

function LinkRow({ pid, lk, onChange }) {
  const send = useSend(pid, lk, onChange);
  const sends = lk.sends || [];
  const last = [...sends].reverse().find((s) => s.channel === "whatsapp" || s.channel === "email") || [...sends].reverse().find((s) => s.channel === "consultor");
  const sent = sends.some((s) => s.channel === "whatsapp" || s.channel === "email");
  const reach = lk.has_phone || lk.has_email;
  return (
    <div className="rounded-xl border border-slate-100 p-3 space-y-2" data-testid={`portal-link-${ids(lk)}`}>
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-medium text-ec-green truncate">{lk.name || "(sem nome)"}</span>
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border border-slate-200 text-slate-500" data-testid={`portal-lang-${ids(lk)}`}>{lk.lang === "en" ? "EN" : "PT"}</span>
        <span className="text-[11px] text-slate-400">{ROLE[lk.role]}</span>
      </div>
      {!reach && (
        <div className="flex gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-2.5 py-2" data-testid={`portal-nocontact-${ids(lk)}`}>
          <AlertTriangle size={13} className="shrink-0 mt-0.5" /> Sem contacto do cliente. Acrescente um telemóvel ou email.
        </div>
      )}
      {lk.auth_mode === "pin" && <div className="text-xs text-slate-600 flex items-center gap-1.5"><KeyRound size={12} /> Código de acesso do cliente: <b className="font-mono">{lk.pin}</b></div>}
      {!sent && reach && <ReadyMessage pid={pid} lk={lk} send={send} title="Mensagem pronta · ainda não foi enviada" testid={`portal-first-${ids(lk)}`} />}
      <SentLine lk={lk} last={last} />
      {sent && <OpenLine lk={lk} />}
      {last && <SentActions lk={lk} send={send} last={last} />}
      {sent && lk.pending_reminder && <ReadyMessage pid={pid} lk={lk} send={send} title="Mensagem pronta · lembrete por enviar" text={lk.pending_reminder.wa_text} testid={`portal-reminder-${ids(lk)}`} />}
    </div>
  );
}

export const PortalLinks = ({ pid, highlight }) => {
  const [data, setData] = useState(null);
  const load = () => api.get(`/processes/${pid}/portal-links`).then(({ data: d }) => setData(d)).catch(() => setData({ links: [] }));
  useEffect(() => { load(); }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (v) => {
    try { await api.put(`/processes/${pid}`, { reminders_enabled: v }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className={`rounded-[16px] ${highlight ? "p-4 border border-ec-gold ring-2 ring-ec-gold/40" : ""}`} data-testid="portal-links-card">
      {!data ? <p className="text-sm text-slate-400">A carregar…</p> : data.links.length === 0 ? (
        <p className="text-sm text-slate-400">Adicione proprietários ou compradores ao processo para gerar as ligações.</p>
      ) : (
        <div className="space-y-3">{data.links.map((lk) => <LinkRow key={lk.token} pid={pid} lk={lk} onChange={load} />)}</div>
      )}
      {data && (
        <label className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-100 text-xs text-slate-600">
          Lembretes automáticos aos 3 e 7 dias
          <Switch checked={data.reminders_enabled !== false} onCheckedChange={toggle} data-testid="reminders-toggle" />
        </label>
      )}
    </div>
  );
};
